import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Wallet, CreditCard, ArrowUpRight, ArrowDownLeft, Settings2,
  TrendingDown, Zap, History, ToggleLeft, ToggleRight, Loader2,
  CheckCircle, XCircle, Infinity, Trash2
} from 'lucide-react';

export default function BillingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [balance, setBalance] = useState(null);
  const [packages, setPackages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-topup
  const [autoTopup, setAutoTopup] = useState({ enabled: false, amount: 50, threshold: 5 });
  const [savingAutoTopup, setSavingAutoTopup] = useState(false);

  // Status banners
  const [banner, setBanner] = useState(null); // { type: 'success'|'error'|'loading', text }

  const loadData = useCallback(async () => {
    try {
      const [balData, pkgData, txData] = await Promise.all([
        api.getBalance(),
        api.getPackages(),
        api.getTransactions({ limit: 50 }),
      ]);
      setBalance(balData);
      setAutoTopup({
        enabled: balData.auto_topup_enabled,
        amount: balData.auto_topup_amount,
        threshold: balData.auto_topup_threshold,
      });
      setPackages(pkgData.packages || []);
      setTransactions(txData.transactions || []);
      setTxTotal(txData.total || 0);
    } catch (err) {
      console.error('Failed to load billing data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const cardSetup = searchParams.get('card_setup');
    const enableAutotopup = searchParams.get('enable_autotopup');

    if (sessionId) {
      setBanner({ type: 'loading', text: 'Checking payment status...' });
      let attempts = 0;
      const poll = async () => {
        try {
          const res = await api.getCheckoutStatus(sessionId);
          if (res.payment_status === 'paid') {
            setBanner({ type: 'success', text: `Payment successful! ${res.credits} Credits added.` });
            setSearchParams({});
            loadData();
            return;
          }
          attempts++;
          if (attempts < 6) setTimeout(poll, 2000);
          else { setBanner({ type: 'error', text: 'Payment status unknown. Check your transactions.' }); setSearchParams({}); }
        } catch {
          setBanner({ type: 'error', text: 'Could not verify payment.' });
          setSearchParams({});
        }
      };
      poll();
    } else if (cardSetup === 'success') {
      setBanner({ type: 'success', text: 'Payment method saved successfully!' });
      setSearchParams({});
      // If we came back from card setup with auto-topup flag, enable it
      if (enableAutotopup === '1') {
        api.updateAutoTopup({ enabled: true, amount: 50, threshold: 5 })
          .then(() => {
            setBanner({ type: 'success', text: 'Card saved & Auto Top-up activated!' });
            loadData();
          })
          .catch(() => loadData());
      } else {
        loadData();
      }
    } else if (cardSetup === 'cancelled') {
      setBanner({ type: 'error', text: 'Card setup cancelled.' });
      setSearchParams({});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const handleCheckout = async (pkgId) => {
    setCheckoutLoading(pkgId);
    setError('');
    try {
      const res = await api.createCheckout(pkgId, window.location.origin);
      if (res.url) window.location.href = res.url;
      else setError('No checkout URL returned.');
    } catch (err) {
      setError(err.message || 'Checkout failed.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleSetupCard = async () => {
    setCardLoading(true);
    setError('');
    try {
      const res = await api.setupCard(window.location.origin);
      if (res.url) window.location.href = res.url;
      else setError('No card setup URL returned.');
    } catch (err) {
      setError(err.message || 'Card setup failed.');
    } finally {
      setCardLoading(false);
    }
  };

  const handleRemoveCard = async () => {
    if (!window.confirm('Remove saved payment method?')) return;
    try {
      await api.removePaymentMethod();
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to remove card.');
    }
  };

  const handleSaveAutoTopup = async () => {
    setSavingAutoTopup(true);
    setError('');
    try {
      await api.updateAutoTopup({
        enabled: autoTopup.enabled,
        amount: parseFloat(autoTopup.amount) || 50,
        threshold: parseFloat(autoTopup.threshold) || 5,
      });
      await loadData();
    } catch (err) {
      // If no card saved, redirect to card setup with auto-topup flag
      if (err.message && err.message.includes('no_card')) {
        const res = await api.setupCard(window.location.origin, true);
        if (res.url) window.location.href = res.url;
      } else {
        setError(err.message || 'Auto-topup update failed.');
      }
    } finally {
      setSavingAutoTopup(false);
    }
  };

  const handleToggleAutoTopup = () => {
    const newEnabled = !autoTopup.enabled;
    // If enabling without a card, redirect to card setup
    if (newEnabled && !pm) {
      setCardLoading(true);
      setError('');
      api.setupCard(window.location.origin, true)
        .then(res => {
          if (res.url) window.location.href = res.url;
          else setError('No card setup URL returned.');
        })
        .catch(err => setError(err.message || 'Card setup failed.'))
        .finally(() => setCardLoading(false));
      return;
    }
    setAutoTopup(prev => ({ ...prev, enabled: newEnabled }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const pm = balance?.payment_method;

  return (
    <div data-testid="billing-page">
      {/* Banner */}
      {banner && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 border ${
          banner.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' :
          banner.type === 'error' ? 'bg-red-500/10 border-red-500/30 text-red-400' :
          'bg-blue-500/10 border-blue-500/30 text-blue-400'
        }`} data-testid="billing-banner">
          {banner.type === 'success' && <CheckCircle className="w-4 h-4" />}
          {banner.type === 'error' && <XCircle className="w-4 h-4" />}
          {banner.type === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />}
          <span className="text-sm">{banner.text}</span>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Billing & Credits
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Manage your credit balance, payment methods, and usage
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Balance */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Current Balance</p>
                    {balance?.unlimited ? (
                      <div className="flex items-center gap-2" data-testid="balance-unlimited">
                        <Infinity className="w-8 h-8 text-primary" />
                        <span className="text-lg text-muted-foreground">Unlimited (Admin)</span>
                      </div>
                    ) : (
                      <p className="text-3xl font-semibold tabular-nums" data-testid="balance-amount" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {balance?.credits?.toFixed(2) || '0.00'}
                        <span className="text-base text-muted-foreground ml-1">Credits</span>
                      </p>
                    )}
                  </div>
                </div>
                {!balance?.unlimited && balance?.credits < 5 && (
                  <Badge variant="destructive" className="text-xs" data-testid="low-balance-badge">
                    <TrendingDown className="w-3 h-3 mr-1" /> Low Balance
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Credit Packages — visible for all non-admin */}
          {!isAdmin && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                  <CreditCard className="w-4 h-4" /> Purchase Credits
                </CardTitle>
              </CardHeader>
              <CardContent>
                {error && (
                  <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400" data-testid="checkout-error">
                    {error}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {packages.map(pkg => (
                    <button
                      key={pkg.id}
                      onClick={() => handleCheckout(pkg.id)}
                      disabled={!!checkoutLoading}
                      data-testid={`package-${pkg.id}`}
                      className="group relative p-4 rounded-lg border border-border hover:border-primary/50 transition-all text-left"
                    >
                      <p className="text-2xl font-semibold font-mono" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {pkg.credits}
                      </p>
                      <p className="text-xs text-muted-foreground">Credits</p>
                      <Separator className="my-2" />
                      <p className="text-sm font-semibold">{pkg.price_eur.toFixed(2)} EUR</p>
                      {checkoutLoading === pkg.id && (
                        <div className="absolute inset-0 bg-background/80 rounded-lg flex items-center justify-center">
                          <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-3">
                  Secure payment via Stripe. Invoice generated per purchase. 1 Credit = 1 EUR.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Transaction History */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                  <History className="w-4 h-4" /> Transactions
                </CardTitle>
                <Badge variant="outline" className="text-xs font-mono">{txTotal} total</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No transactions yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {transactions.map(tx => (
                    <div key={tx.id} data-testid={`tx-${tx.type}`} className="flex items-center justify-between p-3 rounded-lg border border-border">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded flex items-center justify-center ${tx.amount > 0 ? 'bg-emerald-500/10' : 'bg-red-400/10'}`}>
                          {tx.amount > 0 ? <ArrowDownLeft className="w-4 h-4 text-emerald-400" /> : <ArrowUpRight className="w-4 h-4 text-red-400" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{tx.description || tx.type}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {tx.receipt_url && (
                          <a
                            href={tx.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline"
                            data-testid={`receipt-${tx.id}`}
                          >
                            Receipt
                          </a>
                        )}
                        <div className="text-right">
                          <p className={`text-sm font-mono font-semibold tabular-nums ${tx.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(4)}
                          </p>
                          <p className="text-xs text-muted-foreground font-mono tabular-nums">{tx.balance_after.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Payment Method Card */}
          {!isAdmin && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                  <CreditCard className="w-4 h-4" /> Payment Method
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {pm ? (
                  <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm font-medium capitalize" data-testid="card-brand">{pm.brand} ****{pm.last4}</p>
                        <p className="text-xs text-muted-foreground">Expires {pm.exp_month}/{pm.exp_year}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleRemoveCard} data-testid="remove-card-btn">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground mb-3">No payment method saved</p>
                  </div>
                )}
                <Button
                  className="w-full"
                  variant={pm ? 'outline' : 'default'}
                  onClick={handleSetupCard}
                  disabled={cardLoading}
                  data-testid="setup-card-btn"
                >
                  {cardLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                  {pm ? 'Change Card' : 'Add Card'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Auto Top-up */}
          {!isAdmin && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                  <Settings2 className="w-4 h-4" /> Auto Top-up
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  className="flex items-center justify-between p-3 rounded-lg border border-border transition-colors cursor-pointer hover:bg-secondary/30"
                  onClick={handleToggleAutoTopup}
                  data-testid="auto-topup-toggle"
                >
                  <span className="text-sm font-medium">Enable Auto Top-up</span>
                  {autoTopup.enabled ? <ToggleRight className="w-6 h-6 text-primary" /> : <ToggleLeft className="w-6 h-6 text-muted-foreground" />}
                </div>

                {!pm && !autoTopup.enabled && (
                  <p className="text-xs text-muted-foreground">Clicking activates card setup first.</p>
                )}

                {autoTopup.enabled && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="auto-amount" className="text-xs text-muted-foreground">Top-up Amount (Credits)</Label>
                        <Input id="auto-amount" type="number" step="1" min="1" value={autoTopup.amount}
                          onChange={(e) => setAutoTopup(prev => ({ ...prev, amount: e.target.value }))} className="font-mono" data-testid="auto-topup-amount" />
                      </div>
                      <div>
                        <Label htmlFor="auto-threshold" className="text-xs text-muted-foreground">Trigger Below</Label>
                        <Input id="auto-threshold" type="number" step="0.5" min="0" value={autoTopup.threshold}
                          onChange={(e) => setAutoTopup(prev => ({ ...prev, threshold: e.target.value }))} className="font-mono" data-testid="auto-topup-threshold" />
                      </div>
                    </div>
                  </>
                )}

                <Button className="w-full" variant="secondary" onClick={handleSaveAutoTopup} disabled={savingAutoTopup || !autoTopup.enabled} data-testid="save-auto-topup-btn">
                  {savingAutoTopup ? 'Saving...' : 'Save Settings'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Pricing */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <Zap className="w-4 h-4" /> Pricing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-primary/5 border border-primary/20">
                <span className="text-sm font-medium">Token Price</span>
                <span className="font-mono text-sm font-semibold text-primary">
                  {balance?.customer_price_per_1k || '0.015'} Credits / 1K
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-3">All AI operations billed at a flat token rate. 1 Credit = 1 EUR.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
